import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FiArrowRight, FiArrowUpRight, FiCheckCircle } from "react-icons/fi";
import scoraIcon from "../assets/product-icons/scora.png";
import spendnoteIcon from "../assets/product-icons/spendnote.png";
import gridlockIcon from "../assets/product-icons/gridlock.png";
import heroRocketImage from "../assets/code7s-rocket-hero.jpg";
import businessInfographic from "../assets/code7s-business-infographic.jpg";

const services = [
  {
    title: "Product Design",
    desc: "Startup product design, UX UI strategy, user flows, prototypes and interface systems for apps, websites, SaaS platforms and MVP launches.",
    link: "/services/uiux",
  },
  {
    title: "Web & SaaS Development",
    desc: "Web development for startup websites, SaaS platforms, dashboards, admin tools, portals and conversion focused product experiences.",
    link: "/services/web",
  },
  {
    title: "Mobile App Development",
    desc: "Mobile app development for iOS and Android products with polished UX, reliable architecture, MVP planning and launch support.",
    link: "/services/mobile",
  },
];

const products = [
  {
    name: "Scora",
    desc: "A fast document search and knowledge product for finding what matters.",
    icon: scoraIcon,
    link: "/products/scora",
  },
  {
    name: "SpendNote",
    desc: "A clean expense tracker built around speed, habits, and financial clarity.",
    icon: spendnoteIcon,
    link: "/products/spendnote",
  },
  {
    name: "Grid Lock",
    desc: "A compact puzzle game published under the Code7s product label.",
    icon: gridlockIcon,
    link: "/products/gridlock",
  },
];

const process = [
  ["01", "Shape", "Clarify the idea, audience, use cases, launch scope, and business goal."],
  ["02", "Design", "Turn rough thinking into flows, screens, design systems, and a product-ready blueprint."],
  ["03", "Build", "Develop the app, website, SaaS platform, or internal tool with modern engineering standards."],
  ["04", "Launch", "Ship the product, refine the experience, and support the next version after release."],
];

export default function Home() {
  const navigate = useNavigate();

  return (
    <main className="premium-page">
      <section className="premium-shell flex min-h-[78vh] flex-col items-center pb-20 pt-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65 }}
          className="w-full"
        >
          <p className="eyebrow">Code7s studio platform</p>
          <h1 className="hero-heading mx-auto mt-5 2xl:whitespace-nowrap">
            We design, build, and launch startup ideas.
          </h1>
          <p className="mx-auto mt-7 max-w-3xl text-lg leading-8 text-neutral-700 md:text-xl">
            Code7s is a startup product studio for founders and businesses. We
            design, develop and launch mobile apps, websites, SaaS platforms,
            MVPs, ecommerce products, dashboards, internal tools and original
            apps published under Code7s.
          </p>

          <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-neutral-950 px-6 py-3.5 text-sm font-bold text-white shadow-[0_18px_50px_rgba(17,17,17,0.2)] transition hover:-translate-y-0.5 hover:bg-neutral-800"
            >
              Start your product
              <FiArrowUpRight aria-hidden="true" />
            </button>
            <button
              onClick={() => navigate("/portfolio")}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-neutral-950/15 bg-white/65 px-6 py-3.5 text-sm font-bold text-neutral-950 backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white"
            >
              View work
              <FiArrowRight aria-hidden="true" />
            </button>
          </div>

        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-12 w-full"
        >
          <div className="premium-card overflow-hidden rounded-[30px] p-2 md:rounded-[36px] md:p-3">
            <img
              src={heroRocketImage}
              alt="Rocket launching through coral clouds, representing startup product launches"
              className="h-[280px] w-full rounded-[24px] object-cover object-center sm:h-[360px] md:h-[470px] md:rounded-[28px]"
            />
          </div>
        </motion.div>
      </section>

      <section className="premium-shell py-10">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            ["01", "Startup first execution", "Lean enough for early ideas, rigorous enough for mobile app, web app and SaaS launches."],
            ["02", "Design plus engineering", "A single partner for product strategy, UX UI design, frontend development, backend planning and launch support."],
            ["03", "Publisher mindset", "We also publish Code7s apps, so every product is shaped with usability, retention and real launch quality in mind."],
          ].map(([number, title, desc]) => (
            <div key={title} className="glass-card rounded-[24px] p-6">
              <p className="text-sm font-bold text-neutral-400">{number}</p>
              <h3 className="mt-3 text-xl font-bold tracking-tight">{title}</h3>
              <p className="mt-2 leading-7 text-neutral-600">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="premium-shell py-24">
        <div className="mb-10 flex flex-col items-center gap-5 text-center">
          <div className="w-full">
            <p className="section-kicker">Studio services</p>
            <h2 className="section-heading mt-3">
              Product design, development and launch support for startups.
            </h2>
          </div>
          <button
            onClick={() => navigate("/services")}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-neutral-950/15 bg-white/65 px-5 py-3 text-sm font-bold text-neutral-950 transition hover:bg-white"
          >
            All services <FiArrowRight aria-hidden="true" />
          </button>
        </div>

        <div className="grid gap-5 md:grid-cols-3">
          {services.map((service) => (
            <button
              key={service.title}
              onClick={() => navigate(service.link)}
              className="premium-card rounded-[24px] p-6 text-center"
            >
              <FiCheckCircle className="text-2xl text-emerald-600" aria-hidden="true" />
              <h3 className="mt-8 text-2xl font-bold tracking-tight">{service.title}</h3>
              <p className="mt-4 leading-7 text-neutral-600">{service.desc}</p>
              <span className="mt-8 inline-flex items-center gap-2 text-sm font-bold">
                Explore <FiArrowRight aria-hidden="true" />
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="premium-shell py-20">
        <div className="mb-10">
          <p className="section-kicker">How Code7s works</p>
          <h2 className="section-heading mt-3">
            A clear path from product idea to launch ready software.
          </h2>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_0.9fr]">
          <div className="glass-card overflow-hidden rounded-[30px] p-2">
            <img
              src={businessInfographic}
              alt="Business infographic showing a staged startup launch process"
              className="h-full min-h-[360px] w-full rounded-[24px] object-cover object-center"
            />
          </div>
          <div>
            <div className="grid gap-4">
            {process.map(([number, title, desc]) => (
              <div key={title} className="glass-card rounded-3xl p-5">
                <p className="text-sm font-bold text-[#e77562]">{number}</p>
                <h3 className="mt-4 text-xl font-bold">{title}</h3>
                <p className="mt-3 leading-7 text-neutral-600">{desc}</p>
              </div>
            ))}
            </div>
          </div>
        </div>
      </section>

      <section className="premium-shell py-24">
        <div className="mb-10 flex flex-col items-center gap-5 text-center">
          <div className="w-full">
            <p className="section-kicker">Published by Code7s</p>
            <h2 className="section-heading mt-3">
              Code7s also publishes apps, tools and digital products.
            </h2>
          </div>
          <button
            onClick={() => navigate("/products")}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-neutral-950/15 bg-white/65 px-5 py-3 text-sm font-bold text-neutral-950 transition hover:bg-white"
          >
            See products <FiArrowRight aria-hidden="true" />
          </button>
        </div>

        <div className="grid gap-5 lg:grid-cols-3">
          {products.map((product) => (
            <button
              key={product.name}
              onClick={() => navigate(product.link)}
              className="premium-card group rounded-[24px] p-6 text-center"
            >
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-neutral-950/5">
                <img src={product.icon} alt="" className="h-10 w-10 object-contain" />
              </div>
              <h3 className="mt-8 text-2xl font-bold tracking-tight">{product.name}</h3>
              <p className="mt-4 leading-7 text-neutral-600">{product.desc}</p>
              <span className="mt-8 inline-flex items-center gap-2 text-sm font-bold">
                View product <FiArrowUpRight aria-hidden="true" />
              </span>
            </button>
          ))}
        </div>
      </section>
    </main>
  );
}
